/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TableFooter from '@mui/material/TableFooter';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import { visuallyHidden } from '@mui/utils';

import { List, fetchFavoriteListThunk, removeFavoriteThunk, selectFavoriteState } from './favoriteSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import styles from './FavoriteTable.module.scss';
import RemoveFavoriteConfirmation from './RemoveFavoriteConfirmation';
import DocumentName from '../sharedcomponents/DocumentName/DocumentName';
import { useStateLocalStorage } from '../sharedcomponents/useHooks';
import { ReactComponent as DeleteIcon } from '../sharedcomponents/sidemenu/menuicons/DeleteIcon.svg';

interface HeadCell {
  id: keyof List;
  label: string;
  align: 'left' | 'right';
  sort?: boolean;
}

const favoriteHeadCells: readonly HeadCell[] = [
  {
    id: 'actions',
    label: '',
    align: 'right',
  },
  {
    id: 'title',
    label: 'Topic',
    align: 'left',
    sort: true,
  },
];

function FavoriteTableHead(props: {
  order: 'asc' | 'desc';
  orderBy: string;
  handleSort: (sortBy: keyof List) => void;
}) {
  const { order, orderBy, handleSort } = props;

  return (
    <TableHead>
      <TableRow>
        {favoriteHeadCells.map((cell) => (
          <TableCell
            key={cell.id}
            className={styles.tableHeader}
            align={cell.align}
            sortDirection={orderBy === cell.id ? order : false}
          >
            {cell.sort ? (
              <TableSortLabel
                active={orderBy === cell.id}
                direction={orderBy === cell.id ? order : 'asc'}
                onClick={() => handleSort(cell.id)}
              >
                {cell.label}
                {orderBy === cell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              cell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function FavoriteTable() {
  const { favorites, fetchingFavoriteList } = useAppSelector(selectFavoriteState);
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<List[]>([]);
  const [page, setPage] = useState(0);
  const [clickedRow, setClickedRow] = useState<List>();
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof List>('title');
  const [rowsPerPage, setRowsPerPage] = useStateLocalStorage('favorite.pageSize', 25);

  useEffect(() => {
    setRows(favorites);
  }, [favorites]);

  useEffect(() => {
    setRows([]);
    void dispatch(fetchFavoriteListThunk());
  }, [dispatch]);

  const handleSort = (sortBy: keyof List) => {
    if (sortBy !== orderBy) {
      setOrderBy(sortBy);
      setOrder('asc');
    } else {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onClose = (row?: List) => {
    if (row) {
      void dispatch(removeFavoriteThunk({ documentId: row?.doc_id }));
    }
    setClickedRow(undefined);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(() => {
    const sortedList = [...rows]?.sort(
      (a: List, b: List) => ((a[orderBy] || 0) > (b[orderBy] || 0) ? 1 : -1) * (order === 'asc' ? 1 : -1)
    );
    return sortedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage, order, orderBy, rows]);

  return (
    <Box className={styles.favoriteContainer}>
      <p className={styles.title}>Favorites</p>
      <Box sx={{ pl: 1, pr: 1, background: 'var(--white)' }}>
        <TableContainer className={styles.tableContainer}>
          <Table stickyHeader size="small" aria-labelledby="favorite-table">
            <FavoriteTableHead order={order} orderBy={orderBy} handleSort={handleSort} />
            <TableBody>
              {visibleRows.map((row) => {
                return (
                  <TableRow role="row" tabIndex={-1} key={row.doc_id}>
                    <TableCell className={styles.removeCell}>
                      <Tooltip title="Remove Favorite" placement="top">
                        <IconButton aria-label="remove favorite" onClick={() => setClickedRow(row)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={styles.cells}>
                      <DocumentName title={row.title} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={2} />
                </TableRow>
              )}
              {fetchingFavoriteList && (
                <TableRow>
                  <TableCell align="center" colSpan={4}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {!rows.length && !fetchingFavoriteList && (
              <TableFooter>
                <TableRow>
                  <TableCell className={styles.tableHeader} colSpan={2} align="center">
                    No documents available
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[8, 15, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className={styles.pagination}
        />
        <RemoveFavoriteConfirmation row={clickedRow} open={clickedRow ? true : false} onClose={onClose} />
      </Box>
    </Box>
  );
}
